import {Injectable, NgZone} from '@angular/core';
import {Observable, Observer} from 'rxjs';

import {ExgMapPolylineComponent} from '../../exg-map-polyline/exg-map-polyline.component';
import {ExgMapPolylinePointDirective} from '../../exg-map-polyline-point/exg-map-polyline-point.directive';
import {createMVCEventObservable, MVCEvent} from '../../utils/mvcarray-utils';
import {ExgMapGoogleAPIWrapper} from '../exg-map-google-api-wrapper.service';

@Injectable()
export class ExgMapPolylineManagerService {
  private _polylines: Map<ExgMapPolylineComponent, Promise<google.maps.Polyline>> =
    new Map<ExgMapPolylineComponent, Promise<google.maps.Polyline>>();

  constructor(private _mapsWrapper: ExgMapGoogleAPIWrapper, private _zone: NgZone) {
  }

  private static _convertPoints(line: ExgMapPolylineComponent): google.maps.LatLngLiteral[] {
    const path = line._getPoints().map((point: ExgMapPolylinePointDirective) => {
      return {lat: point.latitude, lng: point.longitude} as google.maps.LatLngLiteral;
    });
    return path;
  }

  private static _convertPath(path: keyof typeof google.maps.SymbolPath | string): google.maps.SymbolPath | string {
    const symbolPath = google.maps.SymbolPath[path as keyof typeof google.maps.SymbolPath];
    if (typeof symbolPath === 'number') {
      return symbolPath;
    } else {
      return path;
    }
  }

  private static _convertIcons(line: ExgMapPolylineComponent): Array<google.maps.IconSequence> {
    const icons = line._getIcons().map(icon => ({
      fixedRotation: icon.fixedRotation,
      offset: icon.offset,
      repeat: icon.repeat,
      icon: {
        anchor: new google.maps.Point(icon.anchorX, icon.anchorY),
        fillColor: icon.fillColor,
        fillOpacity: icon.fillOpacity,
        path: ExgMapPolylineManagerService._convertPath(icon.path),
        rotation: icon.rotation,
        scale: icon.scale,
        strokeColor: icon.strokeColor,
        strokeOpacity: icon.strokeOpacity,
        strokeWeight: icon.strokeWeight,
      },
    } as google.maps.IconSequence));
    // prune undefineds;
    icons.forEach(icon => {
      Object.entries(icon).forEach(([key, val]) => {
        if (typeof val === 'undefined') {
          delete (icon as any)[key];
        }
      });
      if (typeof icon?.icon?.anchor?.x === 'undefined' ||
        typeof icon.icon.anchor.y === 'undefined') {
        delete icon?.icon?.anchor;
      }
    });
    return icons;
  }

  addPolyline(line: ExgMapPolylineComponent): void {
    const polylinePromise = this._mapsWrapper.getNativeMap()
      .then(() => [ExgMapPolylineManagerService._convertPoints(line),
        ExgMapPolylineManagerService._convertIcons(line)])
      // @ts-ignore
      .then(([path, icons]: [google.maps.LatLngLiteral[], google.maps.IconSequence[]]) =>
        this._mapsWrapper.createPolyline({
          clickable: line.clickable,
          draggable: line.draggable,
          editable: line.editable,
          geodesic: line.geodesic,
          strokeColor: line.strokeColor,
          strokeOpacity: line.strokeOpacity,
          strokeWeight: line.strokeWeight,
          visible: line.visible,
          zIndex: line.zIndex,
          path,
          icons,
        }));
    this._polylines.set(line, polylinePromise);
  }

  updatePolylinePoints(line: ExgMapPolylineComponent): Promise<void> {
    const path = ExgMapPolylineManagerService._convertPoints(line);
    const m = this._polylines.get(line);
    if (m == null) {
      return Promise.resolve();
    }
    return m.then((l) => this._zone.run(() => l.setPath(path)));
  }

  async updateIconSequences(line: ExgMapPolylineComponent): Promise<void> {
    await this._mapsWrapper.getNativeMap();
    const icons = ExgMapPolylineManagerService._convertIcons(line);
    const m = this._polylines.get(line);
    if (m == null) {
      return;
    }
    return m.then(l => this._zone.run(() => l.setOptions({icons})));
  }

  setPolylineOptions(line: ExgMapPolylineComponent, options: { [propName: string]: any }):
    Promise<void> {
    // @ts-ignore
    return this._polylines.get(line).then((l: google.maps.Polyline) => {
      l.setOptions(options);
    });
  }

  deletePolyline(line: ExgMapPolylineComponent): Promise<void> {
    const m = this._polylines.get(line);
    if (m == null) {
      return Promise.resolve();
    }
    return m.then((l: google.maps.Polyline) => {
      return this._zone.run(() => {
        l.setMap(null);
        this._polylines.delete(line);
      });
    });
  }

  private async getMVCPath(exgMapPolyline: ExgMapPolylineComponent): Promise<google.maps.MVCArray<google.maps.LatLng>> {
    const polyline = await this._polylines.get(exgMapPolyline);
    // @ts-ignore
    return polyline.getPath();
  }

  async getPath(exgMapPolyline: ExgMapPolylineComponent): Promise<google.maps.LatLng[]> {
    return (await this.getMVCPath(exgMapPolyline)).getArray();
  }

  createEventObservable<T>(eventName: string, line: ExgMapPolylineComponent): Observable<T> {
    return new Observable((observer: Observer<T>) => {
      // @ts-ignore
      this._polylines.get(line).then((l: google.maps.Polyline) => {
        l.addListener(eventName, (e: T) => this._zone.run(() => observer.next(e)));
      });
    });
  }

  async createPathEventObservable(line: ExgMapPolylineComponent): Promise<Observable<MVCEvent<google.maps.LatLng>>> {
    const mvcPath = await this.getMVCPath(line);
    return createMVCEventObservable(mvcPath);
  }
}
